// Componente Chart compartilhado entre TelaInterna e Dashboard widget.
// Tipos: bar / barh / bar-stacked / line / area / donut(alias pie) / scatter /
//        bubble / radar / heatmap / treemap / histogram / boxplot / kpi / kpi-spark.
// Todos interativos: hover destaca o elemento + tooltip dark seguindo o cursor. SVG a mao (sem libs).
//
// META + % + CORES (diretoria): cfg.meta = coluna da meta (o SQL traz valor e meta).
//   - bar/barh: desenha a meta na MESMA barra (linha de meta) e colore a barra pela
//     faixa de % atingido (valor/meta). cfg.metaCfg = { lim1, lim2, cor1, cor2, cor3 }.
//   - rotulos (cfg.rotulos): mostra o valor (e a % quando ha meta) em cima da barra/ponto.
//   - donut: a legenda mostra a % de cada fatia (antes so no tooltip).
//   - barh: cfg.grupo/cfg.eixoGrupo (coluna) desenha um divisor + rotulo entre blocos
//     quando o valor dessa coluna muda de uma linha pra outra (linhas ja vem ordenadas
//     por grupo do SQL). Opcional: sem cfg.grupo o render fica identico ao de sempre.
//   - bar: cfg.semEixo esconde a escala/grade do eixo Y (faz sentido quando cada coluna
//     ja mostra o proprio valor) e devolve a folga da esquerda para as colunas.
import React, { useState, useRef } from 'react';

export const PALETA = ['#0a0a0a', '#7a5c3e', '#9c8265', '#bda287', '#6b7280', '#b08968', '#d4a574', '#5f5f5f'];

// Faixas padrao de cor pela % da meta (editavel no widget): <70 vermelho, 70-99 ambar, >=100 verde.
export const META_CFG_PADRAO = { lim1: 70, lim2: 100, cor1: '#FF1744', cor2: '#FFC400', cor3: '#00C853' };

export function fmt(v) {
    if (v == null) return '';
    const n = Number(v);
    if (!isFinite(n)) return String(v);
    if (Math.abs(n) >= 1000) return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
    return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

const num = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };

function Tooltip({ x, y, label, value, sub }) {
    if (x == null) return null;
    return (
        <div style={{
            position: 'absolute', left: x + 12, top: y - 8,
            background: '#0a0a0a', color: '#fff', padding: '9px 13px',
            fontSize: 17, fontFamily: 'monospace', whiteSpace: 'nowrap',
            pointerEvents: 'none', zIndex: 10, lineHeight: 1.45,
            boxShadow: '0 2px 10px rgba(0,0,0,.28)'
        }}>
            <div style={{ opacity: .75, fontSize: 12.5, letterSpacing: '.05em' }}>{label}</div>
            <div style={{ fontWeight: 700 }}>{value}</div>
            {sub && <div style={{ opacity: .7, fontSize: 12.5 }}>{sub}</div>}
        </div>
    );
}

// cfg = { tipo, titulo, x, y, serie, valor, size, meta, rotulos, metaCfg, grupo }
export default function Chart({ cfg, rows, compact = false }) {
    const [hover, setHover] = useState(null);
    const [hiddenSeries, setHiddenSeries] = useState({});
    const wrapRef = useRef(null);

    const tipoRaw = cfg.tipo || cfg.graficoTipo || 'bar';
    const tipo = tipoRaw === 'pie' ? 'donut' : tipoRaw;
    const eX = cfg.x || cfg.eixoX;
    const eY = cfg.y || cfg.eixoY;
    const eSerie = cfg.serie;
    const eValor = cfg.valor;
    const eSize = cfg.size;
    const eMeta = cfg.meta || cfg.eixoMeta || null;     // coluna da meta (opcional)
    const rotulos = !!cfg.rotulos;                        // rotulos de valor nas barras/linhas
    const mcfg = cfg.metaCfg || META_CFG_PADRAO;
    // Ajustes OPT-IN (aditivos): sem esses campos no cfg do widget, tudo renderiza igual a antes.
    const esc = (() => { const n = Number(cfg.escala); return isFinite(n) && n > 1 ? Math.min(n, 3) : 1; })();  // zoom uniforme do SVG (grafico e fontes maiores)
    const kpiPar = !!cfg.kpiPar;              // kpi: valor e meta empilhados no MESMO tamanho
    const mostrarTotal = !!cfg.mostrarTotal;  // barh: somatorio geral (e por grupo, quando ha eixoGrupo)
    // escalaFonte (1..2): aumenta SO os rotulos (eixos, valores, %), sem esticar a geometria.
    // As folgas (padL/padT/padB) e o espacamento dos rotulos do eixo X acompanham, para nao sobrepor.
    const ef = (() => { const n = Number(cfg.escalaFonte); return isFinite(n) && n > 1 ? Math.min(n, 2) : 1; })();
    const fs = (px) => Math.round(px * ef * 10) / 10;
    // kpiEscala (1..2.5): multiplica o tamanho do NUMERO do kpi, para cards de 1 valor so
    // terem o mesmo peso visual dos cards de 2 valores (kpiPar).
    const ke = (() => { const n = Number(cfg.kpiEscala); return isFinite(n) && n > 1 ? Math.min(n, 2.5) : 1; })();
    const estiloV = ke > 1 ? { fontSize: Math.round(44 * ke) } : undefined;
    // altura (1..3): multiplica a ALTURA do grafico sem mexer na largura. Necessario porque o
    // zoom (escala) mexe nas duas ao mesmo tempo: mais zoom = mais alto, mas as folgas dos
    // eixos sao fixas, entao a area das colunas encolhe e as barras ficam MAGRAS.
    const alt = (() => { const n = Number(cfg.altura); return isFinite(n) && n > 1 ? Math.min(n, 3) : 1; })();
    // eixoCurto: rotulos do eixo Y abreviados (157k / 1,9M). Encurta a folga da esquerda e
    // devolve essa largura para as colunas.
    const eixoCurto = !!cfg.eixoCurto;
    // rodape do barh: rotulo custom e, quando somar as linhas NAO faz sentido (contagem de
    // distintos, por ex.), o total pode vir de uma coluna do proprio resultado.
    const totalColuna = cfg.totalColuna || null;
    const rotuloTotal = cfg.rotuloTotal || 'TOTAL';
    const total2Coluna = cfg.total2Coluna || null;
    const rotuloTotal2 = cfg.rotuloTotal2 || 'META DO MÊS';
    // coluna extra: um SEGUNDO indicador exibido como rotulo na barra, para juntar duas
    // metricas de unidades diferentes (R$ e quantidade) no mesmo grafico sem dois eixos.
    const colExtra = cfg.colunaExtra || null;
    const rotExtra = cfg.rotuloExtra || '';
    // mostrarEixo: desenha o eixo de VALORES no barh (grade vertical + escala embaixo +
    // linha de base). O barh nascia sem eixo nenhum, so com as barras e os rotulos.
    const mostrarEixo = !!cfg.mostrarEixo;
    // semEixo: o oposto, para o bar - esconde a escala/grade do eixo Y (que nasce sempre
    // ligada). Limpa o grafico quando cada coluna ja exibe o proprio valor, e a folga da
    // esquerda volta para as colunas.
    const semEixo = !!cfg.semEixo;
    const fmtCurto = (v) => { const n = Number(v); if (!isFinite(n)) return fmt(v); const a = Math.abs(n);
        if (a >= 1e6) return (n / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'M';
        if (a >= 1000) return Math.round(n / 1000).toLocaleString('pt-BR') + 'k';
        return fmt(n); };
    const fmtEixo = eixoCurto ? fmtCurto : fmt;
    const charsEixoY = eixoCurto ? 4 : 7;
    const rotuloValor = cfg.rotuloValor || 'realizado';
    const rotuloMeta = cfg.rotuloMeta || 'meta';
    // metaParte: a coluna apontada em eixoMeta NAO e meta - e uma PARTE do valor da barra
    // (ex.: da carteira de 200 clientes, 120 foram atendidas no mes). Com a flag ligada o barh
    // desenha a parte CHEIA por dentro da barra e a % vira parte/todo - nunca passa de 100%.
    // Sem a flag nada muda: quem usa meta de verdade segue igual.
    const metaParte = !!cfg.metaParte;
    const titulo = cfg.titulo;
    const Head = () => (titulo ? <div className="widget-head"><span className="widget-eyebrow">{titulo}</span></div> : null);

    // Cor pela faixa de % atingido da meta.
    const corMeta = (pct) => {
        if (pct == null || !isFinite(pct)) return PALETA[0];
        const lim1 = mcfg.lim1 != null ? mcfg.lim1 : 70;
        const lim2 = mcfg.lim2 != null ? mcfg.lim2 : 100;
        if (pct < lim1) return mcfg.cor1 || '#FF1744';
        if (pct < lim2) return mcfg.cor2 || '#FFC400';
        return mcfg.cor3 || '#00C853';
    };
    const pctDe = (v, m) => (m && isFinite(m) && m !== 0) ? (v / m) * 100 : null;
    // Cor de TEXTO que se le em cima de um fundo: o rotulo dentro da coluna nascia sempre
    // branco, o que funciona na coluna preta padrao e some na AMARELA (#FFC400) do semaforo
    // de meta. Luminancia perceptual (299/587/114); acima de 0.6 o texto vira escuro.
    const corSobre = (hex) => {
        const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || ''));
        if (!m) return '#fff';
        const n = parseInt(m[1], 16);
        const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
        return L > 0.6 ? '#0a0a0a' : '#fff';
    };

    // Helpers de interatividade (hover destaca + tooltip seguindo o cursor)
    const hoverAt = (e, k, label, value, sub) => {
        if (!wrapRef.current) return;
        const rc = wrapRef.current.getBoundingClientRect();
        setHover({ k, x: e.clientX - rc.left, y: e.clientY - rc.top, label, value, sub });
    };
    const off = () => setHover(null);
    const dim = (k) => (hover && hover.k != null && hover.k !== k ? 0.4 : 1);
    const TIP = (hover && hover.k != null)
        ? <Tooltip x={hover.x} y={hover.y} label={hover.label} value={hover.value} sub={hover.sub}/>
        : null;

    if (!rows || rows.length === 0) return <div className="muted" style={{ fontSize: 12 }}>Sem dados.</div>;
    // Total do rodape (bar e barh): soma das linhas ou, se cfg.totalColuna existir, o valor dessa
    // coluna (para metricas em que somar as linhas nao faz sentido, ex. contagem de distintos).
    const totalRodape = !mostrarTotal ? null
        : (totalColuna && rows[0][totalColuna] != null ? num(rows[0][totalColuna])
            : rows.reduce((a, r) => a + num(r[eY]), 0));
    // Segunda linha do rodape (opt-in): um valor de REFERENCIA que nao e a soma das colunas --
    // hoje, a meta do MES inteiro embaixo do faturamento do periodo filtrado. Sai de uma coluna
    // do proprio SELECT (cfg.total2Coluna), lida da primeira linha. Sem a config, o rodape e
    // exatamente o que era.
    const total2 = (total2Coluna && rows[0] && rows[0][total2Coluna] != null) ? num(rows[0][total2Coluna]) : null;
    const RodapeTotal = () => (mostrarTotal && totalRodape != null ? (
        <div style={{ marginTop: 4, paddingRight: 4, borderTop: '1px solid #e2e2e2', paddingTop: 5 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 8, fontSize: 12.5 }}>
                <span style={{ color: '#888', letterSpacing: '.04em' }}>{rotuloTotal}</span>
                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totalRodape)}</b>
            </div>
            {total2 != null && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 8, fontSize: 11.5, marginTop: 2 }}>
                    <span style={{ color: '#999', letterSpacing: '.04em' }}>{rotuloTotal2}</span>
                    <b style={{ fontVariantNumeric: 'tabular-nums', color: '#555' }}>{fmt(total2)}</b>
                </div>
            )}
        </div>
    ) : null);
    const precisaXY = !['kpi', 'histogram'].includes(tipo);
    if (precisaXY && (!eX || !eY)) return <div className="muted" style={{ fontSize: 12 }}>Configure os eixos X e Y.</div>;

    if (tipo === 'kpi') {
        if (!eY) return <div className="muted" style={{ fontSize: 12 }}>Configure a coluna.</div>;
        const total = rows.reduce((acc, r) => acc + num(r[eY]), 0);
        const totalMeta = eMeta ? rows.reduce((acc, r) => acc + num(r[eMeta]), 0) : null;
        const pct = pctDe(total, totalMeta);
        const Cabeca = () => (titulo ? <div className="widget-head"><span className="widget-eyebrow">{titulo}</span></div> : null);
        // kpiPar: meta e realizado com o MESMO peso visual (mesma classe .v), um sob o outro.
        if (kpiPar && totalMeta != null) {
            return (
                <div className="widget" style={{ minWidth: 160 }}>
                    <Cabeca />
                    <div className="kpi-big">
                        <div>
                            <div className="l" style={{ marginTop: 0 }}>{rotuloValor}</div>
                            <div className="v" style={estiloV}>{fmt(total)}</div>
                        </div>
                        <div>
                            <div className="l" style={{ marginTop: 0 }}>{rotuloMeta}</div>
                            <div className="v" style={estiloV}>{fmt(totalMeta)}</div>
                        </div>
                        {pct != null && (
                            <div className="sub" style={{ fontSize: 14, marginTop: 2, display: 'flex', alignItems: 'baseline', gap: 5 }}>
                                <b style={{ color: corMeta(pct), fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Math.round(pct)}%</b>
                                <span>da {rotuloMeta}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="widget" style={{ minWidth: 160 }}>
                <Cabeca />
                <div className="kpi-big">
                    {cfg.rotuloValor && <div className="l" style={{ marginTop: 0 }}>{cfg.rotuloValor}</div>}
                    <div className="v" style={estiloV}>{fmt(total)}</div>
                    {pct != null
                        ? <div className="sub">meta {fmt(totalMeta)} · <b style={{ color: corMeta(pct) }}>{Math.round(pct)}%</b></div>
                        : (!cfg.rotuloValor && <div className="sub">total de {eY}</div>)}
                </div>
            </div>
        );
    }

    if (tipo === 'bar') {
        const dados = rows.slice(0, 30);
        const vals = dados.map(r => num(r[eY]));
        const metas = eMeta ? dados.map(r => num(r[eMeta])) : null;
        const todos = metas ? vals.concat(metas) : vals;
        const max = Math.max(...todos, 0);
        const min = Math.min(...todos, 0);
        const span = (max - min) || 1;
        const podeRotular = (rotulos || !!eMeta) && dados.length <= 16;
        const W = (compact ? 320 : 480) / esc;
        const H = Math.round((compact ? (podeRotular ? 165 : 140) : (podeRotular ? 225 : 200)) * alt);
        // Altura REAL da fonte de cada rotulo: a pilha (valor / % / extra) e empilhada com
        // esses tamanhos e o padT e reservado a partir deles. Com passo fixo de 11px, o
        // valor e a % colavam um no outro assim que escalaFonte passava de 1.
        const hValor = fs(compact ? 8 : 9);
        const hPct = fs(compact ? 8.5 : 10);
        const hExtra = fs(compact ? 9 : 10);
        const padL = semEixo ? 6 : Math.round(charsEixoY * fs(9) * 0.62 + 6), padR = 6,
            padT = podeRotular ? Math.max(26, Math.round(6 + hValor + 3 + hPct + (colExtra ? 3 + hExtra : 0))) : 6,
            padB = Math.round((compact ? 24 : 30) + (ef - 1) * 12);
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const bw = innerW / dados.length;
        // Vao entre colunas: 2 quando ha espaco, proporcional quando ha muitas barras
        // (com 28 dias o vao fixo de 2 comia ~40% do slot e deixava a coluna magra).
        const vao = Math.min(2, bw * 0.06);
        const yDe = (val) => padT + innerH - ((val - min) / span) * innerH;
        const zeroY = yDe(0);
        const ticksY = [min, min + span * .25, min + span * .5, min + span * .75, max];
        // Rotulos do eixo X: pula de N em N e, se o ULTIMO ponto cair colado no rotulo anterior,
        // SUBSTITUI o anterior em vez de desenhar os dois (antes saia sobreposto, ex. "26/0728/07").
        // Espacamento dos rotulos do eixo X calculado pela largura REAL de cada rotulo
        // (n de caracteres x tamanho da fonte) contra o passo entre barras. Assim funciona
        // com qualquer zoom/fonte/quantidade de barras - antes era um numero fixo de rotulos
        // e, com zoom alto, as datas encostavam (as folgas padL/padR nao encolhem com o zoom).
        const larguraRotX = (compact ? 5 : 8) * fs(9) * 0.62 + 8;
        const skipX = Math.max(1, Math.ceil(larguraRotX / bw));
        const idxRotX = [];
        for (let i = 0; i < dados.length; i += skipX) idxRotX.push(i);
        const ultX = dados.length - 1;
        if (idxRotX.length && idxRotX[idxRotX.length - 1] !== ultX) {
            if (ultX - idxRotX[idxRotX.length - 1] < Math.max(2, Math.round(skipX * 0.7))) idxRotX[idxRotX.length - 1] = ultX;
            else idxRotX.push(ultX);
        }
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {!semEixo && ticksY.map((t, i) => (
                        <g key={i}>
                            <line x1={padL} x2={W - padR} y1={yDe(t)} y2={yDe(t)} stroke="#eee"/>
                            <text x={padL - 4} y={yDe(t) + 3} textAnchor="end" fontSize={fs(9)} fill="#888">{fmtEixo(t)}</text>
                        </g>
                    ))}
                    {dados.map((r, i) => {
                        const v = num(r[eY]);
                        const vy = yDe(v);
                        const barY = Math.min(zeroY, vy);
                        const bh = Math.abs(vy - zeroY);
                        const metaV = metas ? metas[i] : null;
                        const pct = metas ? pctDe(v, metaV) : null;
                        const ativo = hover && hover.idx === i;
                        const cor = metas ? corMeta(pct) : (ativo ? PALETA[1] : PALETA[0]);
                        return (
                            <g key={i}
                                onMouseEnter={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: i, x: e.clientX - rc.left, y: e.clientY - rc.top }); }}
                                onMouseMove={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: i, x: e.clientX - rc.left, y: e.clientY - rc.top }); }}
                                onMouseLeave={() => setHover(null)}>
                                <rect x={padL + i * bw + vao / 2} y={barY} width={Math.max(bw - vao, 1)} height={bh} fill={cor} opacity={ativo ? .85 : 1} style={{ transition: 'fill .12s' }}/>
                                {metas && metaV != null && (
                                    /* marcador da meta: mais grosso e um pouco mais largo que a coluna, para ler de longe */
                                    <line x1={padL + i * bw + vao / 2 - Math.min(0.8, vao / 2)} x2={padL + i * bw + bw - vao / 2 + Math.min(0.8, vao / 2)} y1={yDe(metaV)} y2={yDe(metaV)} stroke="#0a0a0a" strokeWidth={2.6} strokeDasharray="4 2.4"/>
                                )}
                            </g>
                        );
                    })}
                    {podeRotular && dados.map((r, i) => {
                        const v = num(r[eY]);
                        const metaV = metas ? metas[i] : null;
                        const pct = metas ? pctDe(v, metaV) : null;
                        const cx = padL + i * bw + bw / 2;
                        const topo = Math.min(zeroY, yDe(v));
                        const yMeta = metaV != null ? yDe(metaV) : null;
                        const pilha = [{ txt: fmt(v), f: hValor, cor: '#555', peso: '400' }];
                        if (pct != null) pilha.push({ txt: Math.round(pct) + '%', f: hPct, cor: corMeta(pct), peso: '700' });
                        // Rotulo extra: dentro da coluna quando ela e alta o suficiente, senao no
                        // topo da pilha. A fonte encolhe (e, no limite, o rotulo sai) para nao
                        // invadir a coluna vizinha - "458 clientes" era mais largo que o slot.
                        let extra = null;
                        if (colExtra && r[colExtra] != null) {
                            const slot = Math.max(bw - vao - 4, 10);
                            const cabe = (t, f) => t.length * f * 0.58 <= slot;
                            let txt = fmt(r[colExtra]) + (rotExtra ? ' ' + rotExtra : '');
                            let f = hExtra;
                            if (!cabe(txt, f)) {
                                const menor = slot / (txt.length * 0.58);
                                if (menor >= hExtra * 0.8) f = Math.round(menor * 10) / 10;
                                else {
                                    txt = fmt(r[colExtra]);
                                    if (!cabe(txt, f)) f = Math.max(fs(6.5), Math.round(slot / (txt.length * 0.58) * 10) / 10);
                                }
                            }
                            if (Math.abs(yDe(v) - zeroY) > f * 3) extra = { txt, f, y: zeroY - f * 0.9, cor: corSobre(metas ? corMeta(pct) : PALETA[0]) };
                            else pilha.push({ txt, f, cor: '#444', peso: '700' });
                        }
                        // Empilha de baixo para cima com a altura de cada fonte (nao com passo fixo)
                        // e sobe o rotulo que cairia SOBRE o marcador de meta - era o caso da % quando
                        // a meta ficava acima da coluna (o tracejado atravessava o texto).
                        const fugirDaMeta = (yb, f) => (yMeta != null && yMeta > yb - f * 0.85 && yMeta < yb + f * 0.3 + 2)
                            ? yMeta - 3 - f * 0.3 : yb;
                        let y = topo - 4;
                        const rotulados = pilha.map((it, k) => {
                            if (k > 0) y -= it.f + 3;
                            y = fugirDaMeta(y, it.f);
                            return { ...it, y };
                        });
                        return (
                            <g key={'lbl' + i} style={{ pointerEvents: 'none' }}>
                                {rotulados.map((it, k) => (
                                    <text key={k} x={cx} y={it.y} textAnchor="middle" fontSize={it.f} fontWeight={it.peso} fill={it.cor}>{it.txt}</text>
                                ))}
                                {extra && <text x={cx} y={extra.y} textAnchor="middle" fontSize={extra.f} fontWeight="700" fill={extra.cor || '#fff'}>{extra.txt}</text>}
                            </g>
                        );
                    })}
                    {idxRotX.map(i => (
                        <text key={i} x={padL + i * bw + bw / 2} y={H - 6} textAnchor="middle" fontSize={fs(9)} fill="#888">{String(dados[i][eX] || '').slice(0, compact ? 5 : 8)}</text>
                    ))}
                    {/* base das colunas: discreta quando o eixo esta escondido */}
                    <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke={semEixo ? '#ddd' : '#888'}/>
                    {!semEixo && <line x1={padL} x2={padL} y1={padT} y2={padT + innerH} stroke="#888"/>}
                </svg>
                <RodapeTotal />
                {hover && hover.idx != null && (
                    <Tooltip x={hover.x} y={hover.y} label={String(dados[hover.idx][eX] || '—')} value={fmt(dados[hover.idx][eY])}
                        sub={(metas ? `meta ${fmt(metas[hover.idx])}${pctDe(num(dados[hover.idx][eY]), metas[hover.idx]) != null ? ' · ' + Math.round(pctDe(num(dados[hover.idx][eY]), metas[hover.idx])) + '%' : ''}` : eY)
                            + (colExtra && dados[hover.idx][colExtra] != null ? ` · ${fmt(dados[hover.idx][colExtra])} ${rotExtra || colExtra}` : '')}/>
                )}
            </div>
        );
    }

    if (tipo === 'barh') {
        const dados = rows.slice(0, 20);
        const vals = dados.map(r => num(r[eY]));
        const metas = eMeta ? dados.map(r => num(r[eMeta])) : null;
        const max = Math.max(...(metas ? vals.concat(metas) : vals), 0) || 1;
        const W = (compact ? 320 : 480) / esc;
        const rowH = Math.round((compact ? 18 : 24) * (1 + (ef - 1) * 0.7));
        const eGrupo = cfg.grupo || cfg.eixoGrupo || null;
        // Somatorio opt-in: sempre sobre TODAS as linhas do resultado (nao so as 20 exibidas).
        const totalGrupo = {};
        if (mostrarTotal && eGrupo) rows.forEach(r => { const g = String(r[eGrupo]); totalGrupo[g] = (totalGrupo[g] || 0) + num(r[eY]); });
        const dividerH = eGrupo ? Math.round((compact ? 16 : 20) * (1 + (ef - 1) * 0.7)) : 0;
        let cursor = 5;
        const posicoes = dados.map((r, i) => {
            const novoGrupo = !!eGrupo && (i === 0 || String(r[eGrupo]) !== String(dados[i - 1][eGrupo]));
            if (novoGrupo) cursor += dividerH;
            const y = cursor;
            cursor += rowH;
            return { y, novoGrupo };
        });
        const padB = mostrarEixo ? Math.round(fs(9) * 1.7 + 8) : 0;   // espaco da escala embaixo
        const H = cursor + 5 + padB;
        // Folga da esquerda dimensionada pelo rotulo mais longo (limitada a 34% da largura),
        // e o corte do texto derivado dessa folga - antes era 70/120 fixos com corte cego em
        // 9/16 caracteres, que truncava nomes no meio ("Mega 1 (M", "Rose Belf").
        // A folga e o corte saem da MESMA medida (largura util do rotulo): antes o padL era
        // arredondado e o corte recalculado com floor sobre ele, o que comia 1 caractere
        // ("Angela Campo", "Sem vendedor") mesmo quando o nome cabia na folga.
        const chLargura = fs(10) * 0.58;
        const chMax = Math.max(...dados.map(r => String(r[eX] || '').length), 1);
        const larguraRotulo = Math.min(chMax * chLargura, W * 0.34 - 10);
        const padL = Math.round(larguraRotulo) + 10;
        const corteX = Math.max(6, Math.round(larguraRotulo / chLargura));
        const padR = Math.round((metaParte ? 96 : 56) + (ef - 1) * 18);
        const innerW = W - padL - padR;
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {mostrarEixo && (
                        /* grade + escala ANTES das barras, para ficar atras delas */
                        <g style={{ pointerEvents: 'none' }}>
                            {[0, .25, .5, .75, 1].map((f, i) => {
                                const x = padL + f * innerW;
                                return (
                                    <g key={'ex' + i}>
                                        <line x1={x} x2={x} y1={0} y2={H - padB} stroke="#eee"/>
                                        <text x={x} y={H - 4} textAnchor="middle" fontSize={fs(9)} fill="#888">{fmtEixo(f * max)}</text>
                                    </g>
                                );
                            })}
                            <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} stroke="#888"/>
                            <line x1={padL} x2={padL} y1={0} y2={H - padB} stroke="#888"/>
                        </g>
                    )}
                    {dados.map((r, i) => {
                        const v = num(r[eY]);
                        const bw = (v / max) * innerW;
                        const { y, novoGrupo } = posicoes[i];
                        const metaV = metas ? metas[i] : null;
                        const pct = metas ? (metaParte ? (v > 0 ? (metaV / v) * 100 : null) : pctDe(v, metaV)) : null;
                        const ativo = hover && hover.idx === i;
                        const cor = metas ? (metaParte ? (ativo ? PALETA[2] : PALETA[3]) : corMeta(pct)) : (ativo ? PALETA[1] : PALETA[0]);
                        const metaX = metas && metaV != null ? padL + (metaV / max) * innerW : null;
                        return (
                            <g key={i}>
                                {novoGrupo && (
                                    <g style={{ pointerEvents: 'none' }}>
                                        <text x={padL} y={y - dividerH + (compact ? 10 : 12)} fontSize={fs(compact ? 8 : 9)} fontWeight="700" letterSpacing="0.06em" fill="#7a5c3e">{String(r[eGrupo]).toUpperCase()}{mostrarTotal ? '  ·  ' + fmt(totalGrupo[String(r[eGrupo])]) : ''}</text>
                                        <line x1={padL} x2={W - padR} y1={y - 4} y2={y - 4} stroke="#e2e2e2"/>
                                    </g>
                                )}
                                <g
                                    onMouseEnter={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: i, x: e.clientX - rc.left, y: e.clientY - rc.top }); }}
                                    onMouseMove={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: i, x: e.clientX - rc.left, y: e.clientY - rc.top }); }}
                                    onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
                                    <text x={padL - 6} y={y + rowH / 2 + 3} textAnchor="end" fontSize={fs(10)} fill="#444">{String(r[eX] || '').slice(0, corteX)}</text>
                                    <rect x={padL} y={y + 2} width={Math.max(bw, 1)} height={rowH - 6} fill={cor}/>
                                    {metaX != null && (metaParte
                                        ? <rect x={padL} y={y + 2} width={Math.max((metaV / max) * innerW, 1)} height={rowH - 6} fill={corMeta(pct)}/>
                                        : <line x1={metaX} x2={metaX} y1={y} y2={y + rowH - 2} stroke="#0a0a0a" strokeWidth="1.6" strokeDasharray="3 2"/>)}
                                    <text x={padL + Math.max(bw, metaX ? metaX - padL : 0) + 5} y={y + rowH / 2 + 3} fontSize={fs(9)} fill="#555">{fmt(v)}{pct != null ? `  ` : ''}{pct != null ? <tspan fontWeight="700" fill={corMeta(pct)}>{metaParte ? `${fmt(metaV)} (${Math.round(pct)}%)` : `${Math.round(pct)}%`}</tspan> : ''}</text>
                                </g>
                            </g>
                        );
                    })}
                </svg>
                <RodapeTotal />
                {/* ⚠️ o nome da linha de referência sai de cfg.rotuloMeta e só cai em "meta" quando
                    ninguém define. Há gráfico em que ela não é meta nenhuma — a carteira da vendedora,
                    por exemplo — e chamar de meta faz o número ser lido como cobrança. */}
                {hover && hover.idx != null && <Tooltip x={hover.x} y={hover.y} label={String(dados[hover.idx][eX] || '—')} value={fmt(dados[hover.idx][eY])} sub={metas ? `${rotuloMeta} ${fmt(metas[hover.idx])}` : eY}/>}
            </div>
        );
    }

    // Barras AGRUPADAS (lado a lado): uma coluna por serie dentro de cada categoria do X, mesma
    // escala. Nasceu em 02/10/2026 para "Liga: entraram x saíram" por mes — empilhar entrada com
    // saida somaria duas coisas que nao se somam. Valores >= 0; rotulos (mostrarRotulos) em cima.
    // eixoMeta (opcional) vira LINHA na mesma escala (ex.: o total na Liga no fim do mes), com o nome
    // de rotuloMeta — so faz sentido quando e a MESMA unidade das barras (nunca um segundo eixo).
    // Com a linha, as barras usam o par azul/terra validado e a linha fica na tinta preta.
    if (tipo === 'bar-grouped') {
        const cats = [...new Set(rows.map(r => String(r[eX])))].slice(0, 24);
        const series = eSerie ? [...new Set(rows.map(r => String(r[eSerie])))].slice(0, 6) : [eY];
        const valDe = (c, s) => rows.filter(r => String(r[eX]) === c && (!eSerie || String(r[eSerie]) === s)).reduce((a, r) => a + num(r[eY]), 0);
        const grade = cats.map(c => series.map(s => valDe(c, s)));
        const linhaV = eMeta ? cats.map(c => { const r = rows.find(x => String(x[eX]) === c && x[eMeta] != null); return r ? num(r[eMeta]) : null; }) : null;
        const cores = linhaV ? ['#5866a8', '#c07a3c'].concat(PALETA.slice(1)) : PALETA;
        const max = Math.max(1, ...grade.flat(), ...(linhaV || []).filter(v => v != null));
        // ~22 unidades por barra no minimo: com 12 meses x 2 barras + rotulos, 320 encavalava e no
        // celular a letra caia para 8 px. O SVG ganha minWidth = W px e a caixa rola de lado no
        // celular (escala 1:1 ali), ja aberta nos meses mais recentes. Com a linha do total o grafico
        // fica mais alto: as barras de entrada/saida dividem a escala com um total bem maior.
        const W = Math.max((compact ? 320 : 480) / esc, cats.length * series.length * 22 + 40);
        const H = Math.round((compact ? (linhaV ? 210 : 165) : (linhaV ? 250 : 225)) * alt);
        const padL = semEixo ? 6 : Math.round(charsEixoY * fs(9) * 0.62 + 6), padR = 6;
        const padT = rotulos || linhaV ? Math.round(10 + fs(compact ? 9.5 : 10) + 3) : 6;
        const padB = Math.round((compact ? 24 : 30) + (ef - 1) * 12);
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const bw = innerW / cats.length;
        const folga = Math.min(6, bw * 0.18);           // vao entre os grupos
        const cw = Math.max((bw - folga) / series.length - 1, 1); // coluna de cada serie (1px de vao entre elas)
        const yDe = (v) => padT + innerH - (v / max) * innerH;
        const ticksY = [0, max * .25, max * .5, max * .75, max];
        const larguraRotX = (compact ? 5 : 8) * fs(9) * 0.62 + 8;
        const skipX = Math.max(1, Math.ceil(larguraRotX / bw));
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <div className="chart-rola" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
                    ref={el => { if (el && el.scrollWidth > el.clientWidth + 1 && !el.dataset.rolou) { el.scrollLeft = el.scrollWidth; el.dataset.rolou = '1'; } }}>
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible', minWidth: W, display: 'block' }}>
                    {!semEixo && ticksY.map((t, i) => (
                        <g key={i}>
                            <line x1={padL} x2={W - padR} y1={yDe(t)} y2={yDe(t)} stroke="#eee"/>
                            <text x={padL - 4} y={yDe(t) + 3} textAnchor="end" fontSize={fs(9)} fill="#888">{fmtEixo(t)}</text>
                        </g>
                    ))}
                    {cats.map((c, i) => (
                        <g key={i}>
                            {series.map((s, si) => {
                                const v = grade[i][si];
                                const x = padL + i * bw + folga / 2 + si * (cw + 1);
                                const y = yDe(v);
                                const k = i + '_' + si;
                                return (
                                    <g key={si}>
                                        <rect x={x} y={y} width={cw} height={Math.max(padT + innerH - y, 0)} rx={Math.min(2, cw / 3)}
                                            fill={cores[si % cores.length]} opacity={dim(k)} style={{ cursor: 'pointer' }}
                                            onMouseMove={e => hoverAt(e, k, c + (eSerie ? ' · ' + s : ''), fmt(v), eSerie ? s : eY)} onMouseLeave={off}/>
                                        {rotulos && v > 0 && (
                                            <text x={x + cw / 2} y={y - 3} textAnchor="middle" fontSize={fs(compact ? 9.5 : 10)} fontWeight="600" fill="#444" style={{ pointerEvents: 'none' }}>{fmt(v)}</text>
                                        )}
                                    </g>
                                );
                            })}
                            {i % skipX === 0 && (
                                <text x={padL + i * bw + bw / 2} y={H - padB + 13} textAnchor="middle" fontSize={fs(10)} fill="#777">{String(c).slice(0, 8)}</text>
                            )}
                        </g>
                    ))}
                    <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#888"/>
                    {linhaV && (() => {
                        const pts = linhaV.map((v, i) => (v == null ? null : [padL + i * bw + bw / 2, yDe(v), v, i])).filter(Boolean);
                        const vs = pts.map(p => p[2]);
                        const iMax = vs.indexOf(Math.max(...vs)), iMin = vs.indexOf(Math.min(...vs));
                        return (
                            <g>
                                <polyline points={pts.map(p => p[0] + ',' + p[1]).join(' ')} fill="none" stroke="#0a0a0a" strokeWidth={1.8} strokeLinejoin="round" style={{ pointerEvents: 'none' }}/>
                                {pts.map(([x, y, v, ic], j) => (
                                    <g key={j}>
                                        <circle cx={x} cy={y} r={3.2} fill="#0a0a0a" stroke="#fff" strokeWidth={1.5} style={{ cursor: 'pointer' }}
                                            onMouseMove={e => hoverAt(e, 'l' + j, cats[ic] + ' · ' + rotuloMeta, fmt(v), rotuloMeta)} onMouseLeave={off}/>
                                        {(j === 0 || j === pts.length - 1 || j === iMax || j === iMin) && (
                                            <text x={x} y={y - 6} textAnchor="middle" fontSize={fs(compact ? 10 : 10.5)} fontWeight="700" fill="#0a0a0a" stroke="#fff" strokeWidth={2.5} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{fmt(v)}</text>
                                        )}
                                    </g>
                                ))}
                            </g>
                        );
                    })()}
                </svg>
                </div>
                {TIP}
                {(eSerie || linhaV) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 10.5, marginTop: 4 }}>
                        {eSerie && series.map((s, si) => <span key={si} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 9, height: 9, background: cores[si % cores.length] }}/>{s}</span>)}
                        {linhaV && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 14, height: 2, background: '#0a0a0a' }}/>{rotuloMeta}</span>}
                    </div>
                )}
            </div>
        );
    }

    if (tipo === 'bar-stacked') {
        const cats = [...new Set(rows.map(r => String(r[eX])))].slice(0, 20);
        const series = eSerie ? [...new Set(rows.map(r => String(r[eSerie])))].slice(0, 8) : [eY];
        const totalPorCat = cats.map(c => series.reduce((a, s) => a + rows.filter(r => String(r[eX]) === c && (!eSerie || String(r[eSerie]) === s)).reduce((aa, r) => aa + num(r[eY]), 0), 0));
        const max = Math.max(...totalPorCat, 1);
        const W = compact ? 320 : 480, H = compact ? 160 : 220;
        const padL = 40, padR = 6, padT = 6, padB = 40;
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const bw = innerW / cats.length;
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {cats.map((c, i) => {
                        let accH = 0;
                        return (
                            <g key={i}>
                                {series.map((s, si) => {
                                    const v = rows.filter(r => String(r[eX]) === c && (!eSerie || String(r[eSerie]) === s)).reduce((a, r) => a + num(r[eY]), 0);
                                    const h = (v / max) * innerH;
                                    const y = padT + innerH - accH - h;
                                    accH += h;
                                    const k = i + '_' + si;
                                    return <rect key={si} x={padL + i * bw + 1} y={y} width={Math.max(bw - 2, 1)} height={h} fill={PALETA[si % PALETA.length]} opacity={dim(k)} style={{ cursor: 'pointer' }} onMouseMove={e => hoverAt(e, k, c + (eSerie ? ' / ' + s : ''), fmt(v), eSerie ? eSerie : eY)} onMouseLeave={off}/>;
                                })}
                                <text x={padL + i * bw + bw / 2} y={H - padB + 12} textAnchor="middle" fontSize="9" fill="#888">{String(c).slice(0, compact ? 5 : 8)}</text>
                            </g>
                        );
                    })}
                    <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#888"/>
                </svg>
                {TIP}
                {eSerie && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 10.5, marginTop: 4 }}>
                        {series.map((s, si) => <span key={si} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 9, height: 9, background: PALETA[si % PALETA.length] }}/>{s}</span>)}
                    </div>
                )}
            </div>
        );
    }

    if (tipo === 'line' || tipo === 'area' || tipo === 'kpi-spark') {
        const dados = rows.slice(0, 60);
        const vals = dados.map(r => num(r[eY]));
        const metasL = eMeta ? dados.map(r => num(r[eMeta])) : null;
        const todosL = metasL ? vals.concat(metasL) : vals;
        const max = Math.max(...todosL, 1);
        const min = Math.min(...todosL, 0);
        const podeRotular = (rotulos || !!eMeta) && dados.length <= 16 && tipo !== 'kpi-spark';
        const W = (compact ? 320 : 480) / esc;
        const H = Math.round((compact ? 120 : 200) * alt);
        const padL = Math.round(charsEixoY * fs(9) * 0.62 + 6), padR = 6,
            padT = podeRotular ? Math.round(24 + (ef - 1) * 12) : 6,
            padB = Math.round((compact ? 22 : 28) + (ef - 1) * 12);
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const step = innerW / Math.max(vals.length - 1, 1);
        const larguraRotX = (compact ? 5 : 8) * fs(9) * 0.62 + 8;   // mesma regra do 'bar'
        const yDe = (v) => padT + innerH - ((v - min) / Math.max(max - min, 1)) * innerH;
        const pts = vals.map((v, i) => ({ x: padL + i * step, y: yDe(v), v, label: dados[i][eX], meta: metasL ? metasL[i] : null }));
        const ticksY = [min, min + (max - min) * .5, max];
        // Mesma regra do 'bar' para nao sobrepor o rotulo do ultimo ponto do eixo X.
        const skipX = Math.max(1, Math.ceil(larguraRotX / step));
        const idxRotX = [];
        for (let i = 0; i < pts.length; i += skipX) idxRotX.push(i);
        const ultX = pts.length - 1;
        if (idxRotX.length && idxRotX[idxRotX.length - 1] !== ultX) {
            if (ultX - idxRotX[idxRotX.length - 1] < Math.max(2, Math.round(skipX * 0.7))) idxRotX[idxRotX.length - 1] = ultX;
            else idxRotX.push(ultX);
        }
        const areaPath = `M${pts[0].x},${padT + innerH} ` + pts.map(p => `L${p.x},${p.y}`).join(' ') + ` L${pts[pts.length - 1].x},${padT + innerH} Z`;
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {ticksY.map((t, i) => (
                        <g key={i}>
                            <line x1={padL} x2={W - padR} y1={yDe(t)} y2={yDe(t)} stroke="#eee"/>
                            <text x={padL - 4} y={yDe(t) + 3} textAnchor="end" fontSize={fs(9)} fill="#888">{fmtEixo(t)}</text>
                        </g>
                    ))}
                    {tipo === 'area' && <path d={areaPath} fill={PALETA[2]} opacity={.35}/>}
                    {metasL && <polyline points={pts.map((p, i) => `${p.x},${yDe(metasL[i])}`).join(' ')} fill="none" stroke="#0a0a0a" strokeWidth={1} strokeDasharray="3 2" opacity={.7}/>}
                    <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={PALETA[0]} strokeWidth={1.4}/>
                    {pts.map((p, i) => {
                        const corP = metasL ? corMeta(pctDe(p.v, p.meta)) : (hover && hover.idx === i ? PALETA[1] : PALETA[0]);
                        return (
                        <circle key={i} cx={p.x} cy={p.y} r={hover && hover.idx === i ? 4.5 : 3} fill={corP}
                            style={{ cursor: 'pointer', transition: 'r .1s' }}
                            onMouseEnter={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: i, x: e.clientX - rc.left, y: e.clientY - rc.top }); }}
                            onMouseLeave={() => setHover(null)}/>
                        );
                    })}
                    {podeRotular && pts.map((p, i) => {
                        const pctL = metasL ? pctDe(p.v, p.meta) : null;
                        return (
                        <g key={'pl' + i} style={{ pointerEvents: 'none' }}>
                            {pctL != null && <text x={p.x} y={p.y - (compact ? 15 : 17)} textAnchor="middle" fontSize={compact ? 8.5 : 10} fontWeight="700" fill={corMeta(pctL)}>{Math.round(pctL)}%</text>}
                            <text x={p.x} y={p.y - 6} textAnchor="middle" fontSize={compact ? 8 : 9} fill="#555">{fmt(p.v)}</text>
                        </g>
                        );
                    })}
                    {idxRotX.map(i => (
                        <text key={i} x={pts[i].x} y={H - 6} textAnchor="middle" fontSize={fs(9)} fill="#888">{String(pts[i].label || '').slice(0, compact ? 5 : 8)}</text>
                    ))}
                    <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#888"/>
                </svg>
                {hover && hover.idx != null && <Tooltip x={hover.x} y={hover.y} label={String(pts[hover.idx].label || '—')} value={fmt(pts[hover.idx].v)} sub={metasL ? `meta ${fmt(pts[hover.idx].meta)}${pctDe(pts[hover.idx].v, pts[hover.idx].meta) != null ? ' · ' + Math.round(pctDe(pts[hover.idx].v, pts[hover.idx].meta)) + '%' : ''}` : eY}/>}
            </div>
        );
    }

    if (tipo === 'donut') {
        const todos = rows.slice(0, 8).map((r, i) => ({ label: String(r[eX] || `Item ${i+1}`), valor: num(r[eY]), cor: PALETA[i % PALETA.length], idx: i }));
        const totalGeral = todos.reduce((a, d) => a + d.valor, 0) || 1;
        const dados = todos.filter(d => !hiddenSeries[d.idx]);
        const total = dados.reduce((a, d) => a + d.valor, 0) || 1;
        let acc = 0;
        const slices = dados.map((d, i) => {
            const frac = d.valor / total;
            const a0 = acc * 2 * Math.PI;
            const a1 = (acc + frac) * 2 * Math.PI;
            acc += frac;
            const x0 = 60 + 50 * Math.sin(a0), y0 = 60 - 50 * Math.cos(a0);
            const x1 = 60 + 50 * Math.sin(a1), y1 = 60 - 50 * Math.cos(a1);
            const large = frac > 0.5 ? 1 : 0;
            return { d, i, frac, path: `M60 60 L${x0} ${y0} A50 50 0 ${large} 1 ${x1} ${y1} Z` };
        });
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <svg viewBox="0 0 120 120" width={compact ? 110 : 140} height={compact ? 110 : 140}>
                        {slices.map(({ d, i, path, frac }) => (
                            <path key={i} d={path} fill={d.cor} stroke="#fff" strokeWidth={1}
                                style={{ cursor: 'pointer', opacity: hover && hover.idx != null && hover.idx !== d.idx ? .35 : 1, transition: 'opacity .15s' }}
                                onMouseEnter={e => { const rc = wrapRef.current.getBoundingClientRect(); setHover({ idx: d.idx, x: e.clientX - rc.left, y: e.clientY - rc.top, label: d.label, valor: d.valor, frac }); }}
                                onMouseLeave={() => setHover(null)}/>
                        ))}
                        {slices.filter(s => s.frac >= 0.08).map(({ d, i, frac, path }) => {
                            const mid = (slices.slice(0, i).reduce((a, s) => a + s.frac, 0) + frac / 2) * 2 * Math.PI;
                            const lx = 60 + 34 * Math.sin(mid), ly = 60 - 34 * Math.cos(mid);
                            return <text key={'pl' + i} x={lx} y={ly + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none' }}>{Math.round(frac * 100)}%</text>;
                        })}
                        <circle cx="60" cy="60" r="22" fill="var(--paper, #fff)"/>
                    </svg>
                    <div style={{ flex: 1, fontSize: 11 }}>
                        {todos.map(d => {
                            const hidden = !!hiddenSeries[d.idx];
                            const pc = (d.valor / totalGeral) * 100;
                            return (
                                <div key={d.idx} onClick={() => setHiddenSeries({ ...hiddenSeries, [d.idx]: !hidden })}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer', opacity: hidden ? .35 : 1, userSelect: 'none' }}>
                                    <span style={{ width: 9, height: 9, background: d.cor, flexShrink: 0 }}/>
                                    <span style={{ flex: 1, textDecoration: hidden ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                                    <span style={{ color: '#888', fontFamily: 'monospace' }}>{pc.toFixed(1)}%</span>
                                    <span style={{ fontWeight: 600, fontFamily: 'monospace', minWidth: 44, textAlign: 'right' }}>{fmt(d.valor)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {hover && hover.label != null && hover.valor != null && (
                    <Tooltip x={hover.x} y={hover.y} label={hover.label} value={fmt(hover.valor)} sub={`${(hover.frac * 100).toFixed(1)}% do total`}/>
                )}
            </div>
        );
    }

    if (tipo === 'scatter' || tipo === 'bubble') {
        const dados = rows.slice(0, 300);
        const xs = dados.map(r => num(r[eX])), ys = dados.map(r => num(r[eY]));
        const xMax = Math.max(...xs, 1), xMin = Math.min(...xs, 0);
        const yMax = Math.max(...ys, 1), yMin = Math.min(...ys, 0);
        const sizes = eSize ? dados.map(r => num(r[eSize])) : null;
        const sMax = sizes ? Math.max(...sizes, 1) : 1;
        const W = compact ? 320 : 480, H = compact ? 180 : 240;
        const padL = 44, padR = 10, padT = 10, padB = 26;
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const sx = (v) => padL + ((v - xMin) / Math.max(xMax - xMin, 1)) * innerW;
        const sy = (v) => padT + innerH - ((v - yMin) / Math.max(yMax - yMin, 1)) * innerH;
        const ticksY = [yMin, (yMin + yMax) / 2, yMax];
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {ticksY.map((t, i) => (
                        <g key={i}>
                            <line x1={padL} x2={W - padR} y1={sy(t)} y2={sy(t)} stroke="#eee"/>
                            <text x={padL - 4} y={sy(t) + 3} textAnchor="end" fontSize="9" fill="#888">{fmt(t)}</text>
                        </g>
                    ))}
                    {dados.map((r, i) => {
                        const rad = sizes ? 3 + (sizes[i] / sMax) * (compact ? 12 : 18) : 3.2;
                        return <circle key={i} cx={sx(xs[i])} cy={sy(ys[i])} r={rad} fill={PALETA[1]} stroke={PALETA[0]} strokeWidth={.5}
                            opacity={hover && hover.k != null ? (hover.k === i ? .95 : .25) : .55} style={{ cursor: 'pointer', transition: 'opacity .1s' }}
                            onMouseMove={e => hoverAt(e, i, String(dados[i][eX] != null ? eX + ' ' + fmt(xs[i]) : '#' + (i + 1)), fmt(ys[i]), eY + (sizes ? ' · ' + eSize + ' ' + fmt(sizes[i]) : ''))} onMouseLeave={off}/>;
                    })}
                    <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#888"/>
                    <line x1={padL} x2={padL} y1={padT} y2={padT + innerH} stroke="#888"/>
                    <text x={(padL + W - padR) / 2} y={H - 2} textAnchor="middle" fontSize="9" fill="#888">{eX}</text>
                </svg>
                {TIP}
            </div>
        );
    }

    if (tipo === 'radar') {
        const dados = rows.slice(0, 10);
        const vals = dados.map(r => num(r[eY]));
        const max = Math.max(...vals, 1);
        const cx = 90, cy = 90, R = 70, n = dados.length;
        const ang = (i) => (i / n) * 2 * Math.PI - Math.PI / 2;
        const pt = (i, frac) => [cx + R * frac * Math.cos(ang(i)), cy + R * frac * Math.sin(ang(i))];
        const poly = dados.map((r, i) => pt(i, num(r[eY]) / max).join(',')).join(' ');
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg viewBox="0 0 180 180" width={compact ? 150 : 200} style={{ overflow: 'visible' }}>
                    {[0.25, 0.5, 0.75, 1].map((f, gi) => (
                        <polygon key={gi} points={dados.map((_, i) => pt(i, f).join(',')).join(' ')} fill="none" stroke="#eee"/>
                    ))}
                    {dados.map((r, i) => { const [x, y] = pt(i, 1); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#eee"/>; })}
                    <polygon points={poly} fill={PALETA[1]} opacity={.35} stroke={PALETA[0]} strokeWidth={1.4}/>
                    {dados.map((r, i) => { const [x, y] = pt(i, num(r[eY]) / max); return <circle key={i} cx={x} cy={y} r={hover && hover.k === i ? 4.5 : 2.5} fill={PALETA[0]} style={{ cursor: 'pointer' }} onMouseMove={e => hoverAt(e, i, String(r[eX]), fmt(r[eY]), eY)} onMouseLeave={off}/>; })}
                    {dados.map((r, i) => { const [x, y] = pt(i, 1.16); return <text key={i} x={x} y={y} textAnchor="middle" fontSize="8.5" fill="#888">{String(r[eX] || '').slice(0, 8)}</text>; })}
                </svg>
                {TIP}
            </div>
        );
    }

    if (tipo === 'heatmap') {
        const valCol = eValor || eY;
        const cols = [...new Set(rows.map(r => String(r[eX])))].slice(0, 24);
        const linhas = [...new Set(rows.map(r => String(r[eY])))].slice(0, 24);
        const mapa = {};
        let vmax = 0;
        rows.forEach(r => { const k = String(r[eX]) + '||' + String(r[eY]); const v = num(r[valCol]); mapa[k] = (mapa[k] || 0) + v; if (mapa[k] > vmax) vmax = mapa[k]; });
        vmax = vmax || 1;
        const cellW = compact ? 18 : 26, cellH = compact ? 16 : 22, padL = compact ? 60 : 90, padT = 18;
        const W = padL + cols.length * cellW + 6;
        const H = padT + linhas.length * cellH + 6;
        const cor = (v) => { const t = v / vmax; return `rgba(122,92,62,${0.12 + t * 0.85})`; };
        return (
            <div style={{ position: 'relative', overflowX: 'auto' }} ref={wrapRef}>
                <Head />
                <svg width={W} viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {cols.map((c, ci) => <text key={ci} x={padL + ci * cellW + cellW / 2} y={padT - 6} textAnchor="middle" fontSize="8" fill="#888">{c.slice(0, 6)}</text>)}
                    {linhas.map((l, li) => (
                        <g key={li}>
                            <text x={padL - 6} y={padT + li * cellH + cellH / 2 + 3} textAnchor="end" fontSize="9" fill="#444">{l.slice(0, compact ? 8 : 12)}</text>
                            {cols.map((c, ci) => { const v = mapa[c + '||' + l] || 0; const k = ci + '_' + li; return <rect key={ci} x={padL + ci * cellW} y={padT + li * cellH} width={cellW - 1} height={cellH - 1} fill={cor(v)} stroke={hover && hover.k === k ? PALETA[0] : 'transparent'} strokeWidth={1} style={{ cursor: 'pointer' }} onMouseMove={e => hoverAt(e, k, c + ' · ' + l, fmt(v), valCol)} onMouseLeave={off}/>; })}
                        </g>
                    ))}
                </svg>
                {TIP}
            </div>
        );
    }

    if (tipo === 'treemap') {
        const items = rows.slice(0, 40).map((r, i) => ({ label: String(r[eX] || i), value: Math.abs(num(r[eY])), idx: i })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
        const W = compact ? 320 : 480, H = compact ? 200 : 280;
        const out = [];
        const layout = (arr, x, y, w, h) => {
            if (!arr.length) return;
            if (arr.length === 1) { out.push({ ...arr[0], x, y, w, h }); return; }
            const total = arr.reduce((a, d) => a + d.value, 0) || 1;
            let acc = 0, i = 0;
            while (i < arr.length - 1 && acc + arr[i].value < total / 2) { acc += arr[i].value; i++; }
            const g1 = arr.slice(0, i + 1), g2 = arr.slice(i + 1);
            const f = (g1.reduce((a, d) => a + d.value, 0)) / total;
            if (w >= h) { layout(g1, x, y, w * f, h); layout(g2, x + w * f, y, w * (1 - f), h); }
            else { layout(g1, x, y, w, h * f); layout(g2, x, y + h * f, w, h * (1 - f)); }
        };
        layout(items, 0, 0, W, H);
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
                    {out.map((d, i) => (
                        <g key={i} style={{ cursor: 'pointer' }} onMouseMove={e => hoverAt(e, i, d.label, fmt(d.value), eY)} onMouseLeave={off}>
                            <rect x={d.x} y={d.y} width={Math.max(d.w - 2, 0)} height={Math.max(d.h - 2, 0)} fill={PALETA[i % PALETA.length]} opacity={dim(i)}/>
                            {d.w > 46 && d.h > 18 && <text x={d.x + 5} y={d.y + 15} fontSize="10" fill="#fff" style={{ pointerEvents: 'none' }}>{d.label.slice(0, Math.floor(d.w / 7))}</text>}
                            {d.w > 46 && d.h > 30 && <text x={d.x + 5} y={d.y + 28} fontSize="9" fill="#fff" opacity={.8} style={{ pointerEvents: 'none' }}>{fmt(d.value)}</text>}
                        </g>
                    ))}
                </svg>
                {TIP}
            </div>
        );
    }

    if (tipo === 'histogram') {
        const col = eX || eY;
        const vals = rows.map(r => num(r[col])).filter(v => isFinite(v));
        const min = Math.min(...vals, 0), max = Math.max(...vals, 1);
        const nb = Math.min(20, Math.max(5, Math.ceil(Math.sqrt(vals.length))));
        const bw0 = (max - min) / nb || 1;
        const bins = new Array(nb).fill(0);
        vals.forEach(v => { let b = Math.floor((v - min) / bw0); if (b < 0) b = 0; if (b >= nb) b = nb - 1; bins[b]++; });
        const cMax = Math.max(...bins, 1);
        const W = compact ? 320 : 480, H = compact ? 150 : 200;
        const padL = 34, padR = 6, padT = 8, padB = 26;
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const bw = innerW / nb;
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {bins.map((c, i) => {
                        const h = (c / cMax) * innerH;
                        return <rect key={i} x={padL + i * bw + 1} y={padT + innerH - h} width={Math.max(bw - 2, 1)} height={h} fill={PALETA[0]} opacity={dim(i)} style={{ cursor: 'pointer' }}
                            onMouseMove={e => hoverAt(e, i, fmt(min + i * bw0) + ' a ' + fmt(min + (i + 1) * bw0), c, 'qtd')} onMouseLeave={off}/>;
                    })}
                    <text x={padL} y={H - 4} fontSize="9" fill="#888">{fmt(min)}</text>
                    <text x={W - padR} y={H - 4} textAnchor="end" fontSize="9" fill="#888">{fmt(max)}</text>
                    <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#888"/>
                </svg>
                {TIP}
            </div>
        );
    }

    if (tipo === 'boxplot') {
        const grupos = [...new Set(rows.map(r => String(r[eX])))].slice(0, 12);
        const quant = (arr, q) => { const s = [...arr].sort((a, b) => a - b); const pos = (s.length - 1) * q; const b = Math.floor(pos); return s[b] + (s[b + 1] - s[b] || 0) * (pos - b); };
        const stats = grupos.map(g => { const vs = rows.filter(r => String(r[eX]) === g).map(r => num(r[eY])); return { g, min: Math.min(...vs), q1: quant(vs, .25), med: quant(vs, .5), q3: quant(vs, .75), max: Math.max(...vs) }; });
        const allMax = Math.max(...stats.map(s => s.max), 1), allMin = Math.min(...stats.map(s => s.min), 0);
        const W = compact ? 320 : 480, H = compact ? 180 : 240;
        const padL = 40, padR = 10, padT = 8, padB = 30;
        const innerW = W - padL - padR, innerH = H - padT - padB;
        const bw = innerW / grupos.length;
        const sy = (v) => padT + innerH - ((v - allMin) / Math.max(allMax - allMin, 1)) * innerH;
        return (
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <Head />
                <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                    {stats.map((s, i) => {
                        const cx = padL + i * bw + bw / 2; const half = Math.min(bw * .3, 22);
                        return (
                            <g key={i} style={{ cursor: 'pointer' }} onMouseMove={e => hoverAt(e, i, String(s.g), 'mediana ' + fmt(s.med), 'q1 ' + fmt(s.q1) + ' · q3 ' + fmt(s.q3))} onMouseLeave={off} opacity={dim(i)}>
                                <line x1={cx} x2={cx} y1={sy(s.max)} y2={sy(s.q3)} stroke="#888"/>
                                <line x1={cx} x2={cx} y1={sy(s.q1)} y2={sy(s.min)} stroke="#888"/>
                                <line x1={cx - half / 2} x2={cx + half / 2} y1={sy(s.max)} y2={sy(s.max)} stroke="#888"/>
                                <line x1={cx - half / 2} x2={cx + half / 2} y1={sy(s.min)} y2={sy(s.min)} stroke="#888"/>
                                <rect x={cx - half} y={sy(s.q3)} width={half * 2} height={Math.max(sy(s.q1) - sy(s.q3), 1)} fill={PALETA[3]} stroke={PALETA[0]}/>
                                <line x1={cx - half} x2={cx + half} y1={sy(s.med)} y2={sy(s.med)} stroke={PALETA[0]} strokeWidth={1.5}/>
                                <text x={cx} y={H - 8} textAnchor="middle" fontSize="9" fill="#888">{String(s.g).slice(0, compact ? 5 : 9)}</text>
                            </g>
                        );
                    })}
                    <line x1={padL} x2={padL} y1={padT} y2={padT + innerH} stroke="#888"/>
                </svg>
                {TIP}
            </div>
        );
    }

    return <div className="muted">Tipo {tipo} desconhecido</div>;
}
